import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Link2 } from 'lucide-react';

interface ContentRendererProps {
  content: string;
  contentType?: string;
  className?: string;
  onRead?: () => void;
  onUnread?: () => void;
  isRead?: boolean;
  onScrolledToEnd?: (scrolledToEnd: boolean) => void;
}

// Responsive styles
const getResponsiveStyles = () => ({
  // Check if we're on a larger screen
  isDesktop: typeof window !== 'undefined' && window.innerWidth >= 1024,
  isTablet: typeof window !== 'undefined' && window.innerWidth >= 768 && window.innerWidth < 1024,
});

const ContentRenderer: React.FC<ContentRendererProps> = ({
  content,
  contentType = 'text/html',
  className = '',
  onRead,
  onUnread,
  isRead = false,
  onScrolledToEnd
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [iframeTimeout, setIframeTimeout] = useState(false);
  const [externalDocConfirmed, setExternalDocConfirmed] = useState(isRead);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [screenSize, setScreenSize] = useState(getResponsiveStyles());

  // Sync externalDocConfirmed with isRead prop when it changes
  useEffect(() => {
    setExternalDocConfirmed(isRead);
  }, [isRead]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Normalize content type
  const normalizedType = contentType?.toLowerCase() || 'text/html';

  // Track screen size changes
  useEffect(() => {
    const handleResize = () => setScreenSize(getResponsiveStyles());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Timeout for iframe loading - if not loaded in 15 seconds, show fallback
  useEffect(() => {
    if (!iframeLoaded && !iframeError) {
      const timeout = setTimeout(() => {
        if (!iframeLoaded) {
          setIframeTimeout(true);
        }
      }, 5000); // 5 second timeout
      
      return () => clearTimeout(timeout);
    }
  }, [iframeLoaded, iframeError]);

  // Check if content is a URL
  const isUrl = useCallback((str: string) => {
    if (!str) return false;
    try {
      const url = new URL(str);
      return url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
      return /^(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(str);
    }
  }, []);

  // Determine actual content type
  const actualType = isUrl(content) ? 'url' : normalizedType;

  // Handle scroll tracking for inline content
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrolledPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    if (scrolledPercentage >= 0.95 && !scrolledToEnd) {
      setScrolledToEnd(true);
      onScrolledToEnd?.(true);
      onRead?.();
    }
  }, [scrolledToEnd, onRead, onScrolledToEnd]);

  // For short content that doesn't need scrolling
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      if (container.scrollHeight <= container.clientHeight) {
        // Content fits without scrolling, mark as read after brief delay
        const timer = setTimeout(() => {
          setScrolledToEnd(true);
          onScrolledToEnd?.(true);
          onRead?.();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [content, onRead, onScrolledToEnd]);

  // Handle external document confirmation
  const handleExternalDocConfirm = () => {
    setExternalDocConfirmed(true);
    onRead?.();
  };

  // Sanitize URL
  const getSanitizedUrl = (url: string) => {
    try {
      let urlToCheck = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        urlToCheck = `https://${url}`;
      }
      return new URL(urlToCheck).href;
    } catch {
      return url;
    }
  };

  // Responsive iframe height — scales with viewport
  const getIframeHeight = () => {
    if (screenSize.isDesktop) return 'min(600px, 65vh)';
    if (screenSize.isTablet) return 'min(500px, 55vh)';
    return 'min(400px, 50vh)';
  };

  // Responsive content max-height — scales with viewport
  const getContentMaxHeight = () => {
    if (screenSize.isDesktop) return 'min(600px, 65vh)';
    if (screenSize.isTablet) return 'min(500px, 55vh)';
    return 'min(400px, 50vh)';
  };

  // Handle URL type
  if (actualType === 'url' || normalizedType === 'text/url') {
    const sanitizedUrl = getSanitizedUrl(content);
    const isPdfUrl = content?.toLowerCase().endsWith('.pdf');

    // For mobile webviews or when iframe fails, show embedded viewer option
    return (
      <div className={className}>
        <div style={{
          background: '#ffffff',
          borderRadius: screenSize.isDesktop ? '12px' : '8px',
          border: '1px solid #e5e5eb',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: screenSize.isDesktop ? '18px 20px' : '14px 16px',
            borderBottom: '1px solid #e5e5eb',
            display: 'flex',
            alignItems: 'center',
            gap: screenSize.isDesktop ? '12px' : '10px'
          }}>
            <span style={{ color: '#4A4E6A' }}>{isPdfUrl ? <FileText size={screenSize.isDesktop ? 24 : 20} /> : <Link2 size={screenSize.isDesktop ? 24 : 20} />}</span>
            <div>
              <h3 style={{ margin: 0, fontSize: screenSize.isDesktop ? '16px' : '14px', color: '#363a50', fontWeight: 600 }}>
                {isPdfUrl ? 'PDF Document' : 'External Document'}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: screenSize.isDesktop ? '13px' : '12px', color: '#6e6e6e' }}>
                Please review this document carefully
              </p>
            </div>
          </div>

          {/* Embedded iframe for the document */}
          {!iframeError && !iframeTimeout && (
            <div style={{ position: 'relative', minHeight: screenSize.isDesktop ? '500px' : screenSize.isTablet ? '400px' : 'min(300px, 40vh)' }}>
              <iframe
                src={isPdfUrl ? `https://docs.google.com/viewer?url=${encodeURIComponent(sanitizedUrl)}&embedded=true` : sanitizedUrl}
                title="Document Viewer"
                style={{
                  width: '100%',
                  height: getIframeHeight(),
                  border: 'none',
                  display: iframeLoaded ? 'block' : 'none'
                }}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                onLoad={() => setIframeLoaded(true)}
                onError={() => setIframeError(true)}
              />
              {!iframeLoaded && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8fafc',
                  gap: '12px'
                }}>
                  <div style={{ 
                    width: screenSize.isDesktop ? '48px' : '40px', 
                    height: screenSize.isDesktop ? '48px' : '40px', 
                    border: '4px solid #e2e8f0',
                    borderTopColor: '#0ea5e9',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <p style={{ color: '#64748b', fontSize: screenSize.isDesktop ? '16px' : '14px' }}>Loading document...</p>
                  <p style={{ color: '#94a3b8', fontSize: '12px' }}>Please wait, this may take a moment...</p>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              )}
            </div>
          )}

          {/* Timeout fallback - document taking too long */}
          {iframeTimeout && !iframeLoaded && (
            <div style={{
              padding: screenSize.isDesktop ? '32px' : '20px',
              backgroundColor: '#fafafb',
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px', color: '#363a50', fontSize: '14px' }}>Document loading slowly</h4>
              <p style={{ margin: '0 0 16px', color: '#6e6e6e', fontSize: '13px' }}>
                The document is taking longer than expected. Please open it directly.
              </p>
              <a
                href={sanitizedUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleExternalDocConfirm}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: screenSize.isDesktop ? '10px 24px' : '8px 20px',
                  backgroundColor: '#363a50',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: '24px',
                  fontWeight: '500',
                  fontSize: screenSize.isDesktop ? '14px' : '13px'
                }}
              >
                Open Document Directly
              </a>
            </div>
          )}

          {/* Fallback or additional actions */}
          <div style={{
            padding: screenSize.isDesktop ? '16px 20px' : '12px 16px',
            backgroundColor: '#fafafb',
            borderTop: '1px solid #e5e5eb'
          }}>
            {(iframeError || iframeTimeout) ? (
              // Show when iframe fails to load
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: '0 0 16px', color: '#6e6e6e', fontSize: screenSize.isDesktop ? '14px' : '13px' }}>
                  The document could not be displayed here. Please view it externally.
                </p>
                <a
                  href={sanitizedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleExternalDocConfirm}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: screenSize.isDesktop ? '12px 24px' : '10px 20px',
                    backgroundColor: '#363a50',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '24px',
                    fontWeight: '500',
                    fontSize: screenSize.isDesktop ? '14px' : '13px'
                  }}
                >
                  Open Document
                </a>
              </div>
            ) : (
              // Show when iframe loads - hide "Open in New Tab" on mobile to avoid webview navigation issues
              (screenSize.isDesktop || screenSize.isTablet) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: screenSize.isDesktop ? '13px' : '12px', color: '#6e6e6e' }}>
                    Cannot see the document clearly?
                  </span>
                  <a
                    href={sanitizedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleExternalDocConfirm}
                    aria-label="Open document in new tab"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: screenSize.isDesktop ? '8px 16px' : '6px 14px',
                      backgroundColor: '#363a50',
                      color: '#fff',
                      textDecoration: 'none',
                      borderRadius: '24px',
                      fontWeight: '500',
                      fontSize: screenSize.isDesktop ? '13px' : '12px'
                    }}
                  >
                    Open in New Tab →
                  </a>
                </div>
              )
            )}
          </div>

          {/* Confirmation checkbox for external documents — only shown when onRead is provided (ContentRenderer manages read state).
             When parent provides its own checkbox, onRead is omitted and this section is hidden. */}
          {onRead && (
            <div style={{
              padding: screenSize.isDesktop ? '16px 20px' : '12px 16px',
              backgroundColor: externalDocConfirmed ? '#f0faf7' : '#fafafb',
              borderTop: '1px solid ' + (externalDocConfirmed ? '#11936f' : '#e5e5eb'),
              transition: 'background-color 0.3s'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: screenSize.isDesktop ? '12px' : '10px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={externalDocConfirmed}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setExternalDocConfirmed(e.target.checked);
                    if (e.target.checked) {
                      onRead?.();
                    } else {
                      onUnread?.();
                    }
                  }}
                  style={{
                    width: screenSize.isDesktop ? '20px' : '18px',
                    height: screenSize.isDesktop ? '20px' : '18px',
                    marginTop: '2px',
                    accentColor: '#11936f',
                    cursor: 'pointer'
                  }}
                />
                <span style={{
                  fontSize: screenSize.isDesktop ? '14px' : '13px',
                  color: externalDocConfirmed ? '#11936f' : '#363a50',
                  lineHeight: '1.5'
                }}>
                  <strong>I confirm I have read / heard and understood the above.</strong>
                </span>
              </label>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Handle PDF (base64 or data URL)
  if (normalizedType === 'application/pdf' || normalizedType === 'pdf') {
    return (
      <div className={className}>
        <div style={{
          background: '#ffffff',
          borderRadius: screenSize.isDesktop ? '12px' : '8px',
          border: '1px solid #e5e5eb',
          padding: screenSize.isDesktop ? '32px' : '20px',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: screenSize.isDesktop ? '16px' : '12px' }}><FileText size={screenSize.isDesktop ? 56 : 40} /></div>
          <h3 style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: screenSize.isDesktop ? '22px' : '18px' }}>PDF Document</h3>
          <p style={{ margin: '0 0 20px', color: '#7f1d1d', fontSize: screenSize.isDesktop ? '16px' : '14px' }}>
            {screenSize.isDesktop ? 'Click to open the PDF document in a new window' : 'Tap to open the PDF document'}
          </p>
          <a
            href={content}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onRead?.()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: screenSize.isDesktop ? '16px 36px' : '14px 28px',
              backgroundColor: '#dc2626',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '10px',
              fontWeight: '600',
              fontSize: screenSize.isDesktop ? '18px' : '16px'
            }}
          >
            📥 Open PDF
          </a>
        </div>
      </div>
    );
  }

  // Handle Markdown
  if (normalizedType === 'text/markdown' || normalizedType === 'markdown') {
    const htmlContent = content
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');

    return (
      <div
        ref={scrollContainerRef}
        className={className}
        onScroll={handleScroll}
        style={{ 
          maxHeight: getContentMaxHeight(), 
          overflowY: 'auto',
          lineHeight: screenSize.isDesktop ? '1.8' : '1.7', 
          fontSize: screenSize.isDesktop ? '17px' : '15px',
          padding: screenSize.isDesktop ? '24px' : '16px',
          backgroundColor: '#fafafa',
          borderRadius: screenSize.isDesktop ? '12px' : '8px'
        }}
        dangerouslySetInnerHTML={{ __html: `<p>${htmlContent}</p>` }}
      />
    );
  }

  // Handle plain text
  if (normalizedType === 'text/plain' || normalizedType === 'plain' || normalizedType === 'text' || normalizedType === 'plaintext') {
    return (
      <div
        ref={scrollContainerRef}
        className={className}
        onScroll={handleScroll}
        style={{
          maxHeight: getContentMaxHeight(),
          overflowY: 'auto',
          padding: screenSize.isDesktop ? '24px' : '16px',
          backgroundColor: '#fafafa',
          borderRadius: screenSize.isDesktop ? '12px' : '8px'
        }}
      >
        <pre style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          fontFamily: 'inherit',
          fontSize: screenSize.isDesktop ? '17px' : '15px',
          lineHeight: screenSize.isDesktop ? '1.8' : '1.7',
          margin: 0
        }}>
          {content}
        </pre>
      </div>
    );
  }

  // Handle HTML and RICHTEXT
  // Override MS Word inline styles with CSS specificity
  const htmlStyleOverrides = `
    .cr-html-content * { max-width: 100% !important; }
    .cr-html-content span, .cr-html-content p { font-size: inherit !important; font-family: inherit !important; }
    .cr-html-content .MsoNormal { margin: 0 0 8px 0; }
  `;

  return (
    <>
      <style>{htmlStyleOverrides}</style>
      <div
        ref={scrollContainerRef}
        className={`${className} cr-html-content`}
        onScroll={handleScroll}
        style={{
          maxHeight: getContentMaxHeight(),
          overflowY: 'auto',
          overflowX: 'hidden',
          lineHeight: screenSize.isDesktop ? '1.8' : '1.7',
          fontSize: screenSize.isDesktop ? '17px' : '15px',
          padding: screenSize.isDesktop ? '24px' : '16px',
          backgroundColor: '#fafafa',
          borderRadius: screenSize.isDesktop ? '12px' : '8px'
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </>
  );
};

export default ContentRenderer;
